import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Filter, Search, Clock, MapPin } from 'lucide-react';
import { MOCK_ALERTS, AGING_THRESHOLDS } from '../services/mockData';
import authService from '../services/authService';
import '../styles/Alerts.css';

const Alerts = () => {
  const [alerts, setAlerts] = useState(MOCK_ALERTS);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [warehouseFilter, setWarehouseFilter] = useState('ALL');

  const canAcknowledge = authService.hasPermission('edit');

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         alert.warehouse.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = severityFilter === 'ALL' || alert.severity === severityFilter;
    const matchesStatus = statusFilter === 'ALL' || 
                         (statusFilter === 'ACKNOWLEDGED' && alert.acknowledged) ||
                         (statusFilter === 'UNACKNOWLEDGED' && !alert.acknowledged);
    const matchesWarehouse = warehouseFilter === 'ALL' || alert.warehouse === warehouseFilter;
    
    return matchesSearch && matchesSeverity && matchesStatus && matchesWarehouse;
  });

  // Get unique warehouses from alerts
  const warehouses = [...new Set(alerts.map(a => a.warehouse))].sort();

  const handleAcknowledge = (alertId) => {
    if (!canAcknowledge) {
      alert('You do not have permission to acknowledge alerts');
      return;
    }

    setAlerts(alerts.map(alert => 
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    ));
  };

  const handleAcknowledgeAll = () => {
    if (!canAcknowledge) {
      alert('You do not have permission to acknowledge alerts');
      return;
    }

    if (window.confirm('Acknowledge all unacknowledged alerts?')) {
      setAlerts(alerts.map(alert => ({ ...alert, acknowledged: true })));
    }
  };

  const getSeverityIcon = (severity) => {
    return <AlertTriangle size={20} />;
  };

  const getTimeSince = (timestamp) => {
    const now = new Date();
    const alertTime = new Date(timestamp);
    const diffMs = now - alertTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  };

  const unacknowledgedCount = alerts.filter(a => !a.acknowledged).length;
  const criticalCount = alerts.filter(a => a.severity === 'CRITICAL' && !a.acknowledged).length;
  const highCount = alerts.filter(a => a.severity === 'HIGH' && !a.acknowledged).length;

  // Group alerts by warehouse
  const alertsByWarehouse = {};
  alerts.forEach(alert => {
    if (!alertsByWarehouse[alert.warehouse]) {
      alertsByWarehouse[alert.warehouse] = [];
    }
    alertsByWarehouse[alert.warehouse].push(alert);
  });

  return (
    <div className="alerts-page">
      <div className="page-header">
        <div>
          <h1>Alerts & Notifications</h1>
          <p className="page-subtitle">
            {unacknowledgedCount} unacknowledged alert{unacknowledgedCount !== 1 ? 's' : ''}
            {criticalCount > 0 && (
              <span style={{fontWeight: 'bold', color: '#dc2626'}}> • {criticalCount} critical</span>
            )}
            {highCount > 0 && (
              <span style={{fontWeight: '600', color: '#f59e0b'}}> • {highCount} high</span>
            )}
          </p>
        </div>
        {canAcknowledge && unacknowledgedCount > 0 && (
          <button onClick={handleAcknowledgeAll} className="acknowledge-all-btn">
            <CheckCircle size={18} />
            Acknowledge All
          </button>
        )}
      </div>

      {/* Warehouse Summary Cards */}
      <div className="warehouse-alerts-summary">
        <h2>Alerts by Warehouse</h2>
        <div className="warehouse-cards-grid">
          {Object.keys(alertsByWarehouse).map(warehouse => {
            const warehouseAlerts = alertsByWarehouse[warehouse];
            const unacknowledged = warehouseAlerts.filter(a => !a.acknowledged).length;
            const critical = warehouseAlerts.filter(a => a.severity === 'CRITICAL' && !a.acknowledged).length;
            const high = warehouseAlerts.filter(a => a.severity === 'HIGH' && !a.acknowledged).length;

            return (
              <div key={warehouse} className={`warehouse-alert-card ${critical > 0 ? 'has-critical' : ''}`}>
                <div className="warehouse-card-header">
                  <MapPin size={20} />
                  <h3>{warehouse}</h3>
                  {critical > 0 && (
                    <span className="critical-badge" style={{fontWeight: 'bold'}}>{critical}</span>
                  )}
                  {high > 0 && critical === 0 && (
                    <span className="high-badge" style={{background: '#f59e0b', color: 'white', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600'}}>{high}</span>
                  )}
                </div>
                <div className="warehouse-card-body">
                  <div className="alert-count">
                    <span className="count-value">{unacknowledged}</span>
                    <span className="count-label">unacknowledged</span>
                  </div>
                  <div className="alert-breakdown">
                    {warehouseAlerts.filter(a => !a.acknowledged).map(alert => (
                      <div key={alert.id} className="mini-alert">
                        <span className={`severity-dot severity-${alert.severity.toLowerCase()}`}></span>
                        <span className="mini-alert-text">{alert.assetId}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search alerts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <Filter size={18} />
          <select value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)}>
            <option value="ALL">All Severities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="UNACKNOWLEDGED">Unacknowledged</option>
            <option value="ACKNOWLEDGED">Acknowledged</option>
          </select>

          <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)}>
            <option value="ALL">All Warehouses</option>
            {warehouses.map(wh => (
              <option key={wh} value={wh}>{wh}</option>
            ))}
          </select>
        </div>

        <div className="results-count">
          Showing {filteredAlerts.length} of {alerts.length} alerts
        </div>
      </div>

      {/* Alerts List */}
      <div className="alerts-list-container">
        {filteredAlerts.length === 0 ? (
          <div className="no-alerts">
            <AlertTriangle size={48} />
            <p>No alerts found matching your criteria</p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div 
              key={alert.id} 
              className={`alert-card severity-${alert.severity.toLowerCase()} ${alert.acknowledged ? 'acknowledged' : ''}`}
            >
              <div className="alert-card-header">
                <div className="alert-severity-badge">
                  {getSeverityIcon(alert.severity)}
                  <span>{alert.severity}</span>
                </div>
                <div className="alert-type-badge">
                  {alert.type.replace('_', ' ')}
                </div>
              </div>

              <div className="alert-card-body">
                <h3>{alert.message}</h3>
                <div className="alert-details">
                  <div className="alert-detail-item">
                    <span className="detail-label">Asset:</span>
                    <span className="detail-value">{alert.assetName} ({alert.assetId})</span>
                  </div>
                  <div className="alert-detail-item">
                    <span className="detail-label">Warehouse:</span>
                    <span className="detail-value">
                      <MapPin size={14} style={{ marginRight: '4px' }} />
                      {alert.warehouse}
                    </span>
                  </div>
                  {alert.ageDays && (
                    <>
                      <div className="alert-detail-item">
                        <span className="detail-label">Current Age:</span>
                        <span className="detail-value aging-value">
                          <Clock size={14} style={{ marginRight: '4px' }} />
                          {alert.ageDays} days
                        </span>
                      </div>
                      <div className="alert-detail-item">
                        <span className="detail-label">Threshold:</span>
                        <span className="detail-value">{alert.threshold} days</span>
                      </div>
                      <div className="alert-detail-item">
                        <span className="detail-label">Exceeded By:</span>
                        <span className="detail-value exceeded-value">
                          {alert.ageDays - alert.threshold} days
                        </span>
                      </div>
                    </>
                  )}
                  <div className="alert-detail-item">
                    <span className="detail-label">Time:</span>
                    <span className="detail-value">
                      {new Date(alert.timestamp).toLocaleString()} ({getTimeSince(alert.timestamp)})
                    </span>
                  </div>
                </div>
              </div>

              <div className="alert-card-footer">
                {alert.acknowledged ? (
                  <div className="acknowledged-badge">
                    <CheckCircle size={16} />
                    <span>Acknowledged</span>
                  </div>
                ) : (
                  canAcknowledge && (
                    <button 
                      onClick={() => handleAcknowledge(alert.id)}
                      className="acknowledge-btn"
                    >
                      <CheckCircle size={16} />
                      Acknowledge
                    </button>
                  )
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Alerts;

